import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { SelectControlled, InputControlled, Select, Loading } from 'components';
import { samples_OPTIONS } from 'utils/constants';
import { NewPatientStep } from './NewPatientStep';
import { PatientContext } from 'contexts/PatientContext';
import { showError } from 'utils/alerts';
import { editPatientSample, newPatientSample } from 'services/patients';
import { toast } from 'react-toastify';
import { useAuth } from 'contexts/AuthContext';

interface SamplesProps {}

interface IFormInputs {
	sampleDate: string | Date;
	sampleType: string;
}

export const Samples: React.FunctionComponent<SamplesProps> = () => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const { isAdmin } = user;
	const { patientId } = useParams();
	const { patient, setPatient, fetching } = useContext<any>(PatientContext);
	const navigate = useNavigate();

	const schema = yup
		.object({
			sampleDate: yup
				.string()
				.typeError(t('error.mustChooseADate'))
				.required(t('error.mustChooseADate')),
			sampleType: yup.string().required(t('error.mustChooseAnOption')),
		})
		.required();

	const {
		handleSubmit,
		control,
		reset,
		setValue,
		formState: { errors, isValid, dirtyFields },
	} = useForm<IFormInputs>({
		resolver: yupResolver(schema),
		mode: 'onChange',
	});

	const handleOnSubmit: SubmitHandler<IFormInputs> = async (data) => {
		try {
			const modifiedFields = Object.keys(dirtyFields);
			if (modifiedFields.length > 0) {
				if (patientId && patient?.samples?.length > 0) {
					const sampleId = patient.samples[0].id;
					await editPatientSample(sampleId, data);
				} else {
					await newPatientSample({ patientId: patient.id, ...data });
				}
				setPatient({ ...patient, samples: [{ ...data }] });
				toast.success(t('patientEdited'));
			}
		} catch (error) {
			console.log(error);
			showError(t('error.savePatientInfo'));
		}
	};

	return (
		<NewPatientStep
			title={`02.- ${t('samplesPreTreatment')}`}
			prevStep='/demography'
			nextStep='/diagnostic'
			handleOnClickNext={handleSubmit(handleOnSubmit)}
			isValid={isValid}
		>
			{fetching ? (
				<Loading />
			) : (
				<form
					onSubmit={handleSubmit(handleOnSubmit)}
					className='grid lg:grid-cols-4 grid-cols-1 gap-6'
				>
					<InputControlled
						disabled={isAdmin}
						className='lg:col-span-2'
						labelClass='sm'
						type='date'
						label={t('sampleDate')}
						//@ts-ignore
						errors={errors.sampleDate}
						control={control}
						defaultValue={
							patient?.samples?.length > 0 ? patient.samples[0].sampleDate : ''
						}
						name='sampleDate'
					/>
					<SelectControlled
						disabled={isAdmin}
						className='lg:col-span-2'
						name='sampleType'
						labelClass='sm'
						options={samples_OPTIONS}
						label={t('sampleType')}
						//@ts-ignore
						errors={errors.sampleType}
						defaultValue={
							patient?.samples?.length > 0
								? {
										value: patient.samples[0].sampleType,
										label: patient.samples[0].sampleType,
								  }
								: ''
						}
						control={control}
					/>
				</form>
			)}
		</NewPatientStep>
	);
};
